import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useEffect, useState, useCallback, useMemo } from 'react';
import { usePopper as usePopper$1 } from 'react-popper';
import { bottom } from '@popperjs/core';
import ReactFocusLock from 'react-focus-lock';
import './Popper2.js';
import '../Portal/index.js';
import '../mergeRefs/index.js';
import usePopperContext, { PopperContextProvider } from './PopperContext.js';
import mergeRefs from '../mergeRefs/mergeRefs.js';
import Portal from '../Portal/Portal.js';

var _process$env;
function useMockedPopper(_, popperElement, _ref) {
  let {
    onFirstUpdate
  } = _ref;
  useEffect(() => {
    onFirstUpdate === null || onFirstUpdate === void 0 || onFirstUpdate();
  }, [onFirstUpdate, popperElement]);
  return {
    styles: {
      popper: {},
      arrow: {}
    },
    attributes: {
      popper: {}
    }
  };
}
const usePopper = typeof process !== 'undefined' && ((_process$env = process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'test' ? useMockedPopper : usePopper$1;
function useResizeObserver(_ref2) {
  let {
    element,
    handleResize,
    isDisabled
  } = _ref2;
  useEffect(() => {
    if (typeof window.ResizeObserver !== 'undefined' && element && !isDisabled) {
      // eslint-disable-next-line compat/compat
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(element);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [handleResize, element, isDisabled]);
}
function useScroll(_ref3) {
  let {
    element,
    handleScroll,
    isDisabled
  } = _ref3;
  useEffect(() => {
    if (element && !isDisabled) {
      document.addEventListener('scroll', handleScroll, true); // useCapture needed since scroll events don't bubble to the document
      return () => {
        document.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [handleScroll, element, isDisabled]);
}
const Popper = _ref4 => {
  let {
    open = false,
    alwaysInDOM = false,
    placement = bottom,
    anchor: anchorElement,
    popper: popperElement,
    arrow: arrowElement,
    anchorNode: externalAnchorNode,
    portalTarget,
    modifiers: externalModifiers = [],
    isDisabled = false,
    isFocusLocked = false,
    focusLockProps
  } = _ref4;
  const [anchorNode, setAnchorNode] = useState(externalAnchorNode);
  const [popperNode, setPopperNode] = useState();
  const [arrowNode, setArrowNode] = useState();
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    if (externalAnchorNode) {
      setAnchorNode(externalAnchorNode);
    }
  }, [externalAnchorNode]);
  useEffect(() => {
    if (!open) {
      setIsReady(false);
    }
  }, [open]);
  const modifiers = [{
    name: 'arrow',
    enabled: !!arrowNode,
    options: {
      element: arrowNode,
      padding: 6
    }
  }, {
    name: 'eventListeners',
    enabled: open && !isDisabled
  }, {
    name: 'computeStyles',
    options: {
      gpuAcceleration: false
    }
  }, {
    name: 'preventOverflow',
    options: {
      padding: 8
    }
  }, ...externalModifiers];

  // Must be memoized to avoid infinite loop
  const onFirstUpdate = useCallback(() => {
    setIsReady(true);
  }, [setIsReady]);
  const {
    styles,
    attributes,
    update
  } = usePopper(anchorNode, popperNode, {
    modifiers,
    placement,
    onFirstUpdate
  });
  const handleUpdate = useCallback(() => {
    update === null || update === void 0 || update();
  }, [update]);

  // Tell popper.js to update the positioning when the popper element changes size since it only listens for window resizing out of the box.
  // See CFE-1295
  useResizeObserver({
    element: popperNode,
    handleResize: handleUpdate,
    isDisabled: !isReady
  });

  // Tell popper.js to update the positioning when a scroll event occurs
  // Needed due to https://github.com/floating-ui/floating-ui/issues/1267. See CFE-843
  useScroll({
    element: popperNode,
    handleScroll: handleUpdate,
    isDisabled: !isReady
  });
  const {
    isParentAnchorHidden
  } = usePopperContext();

  /*
   * If the parent anchor is hidden via CSS opacity, Popper will not consider it to be hidden.
   * To get around this issue, we set `data-popper-reference-hidden` to `true` for all child Popper elements if any ancestor Popper has `data-popper-reference-hidden` set to `true`.
   */
  const isAnchorHidden = useMemo(() => {
    var _attributes$popper;
    return isParentAnchorHidden || !!(attributes !== null && attributes !== void 0 && (_attributes$popper = attributes.popper) !== null && _attributes$popper !== void 0 && _attributes$popper['data-popper-reference-hidden']);
  }, [isParentAnchorHidden, attributes]);
  const context = useMemo(() => ({
    isParentAnchorHidden: isAnchorHidden
  }), [isAnchorHidden]);
  const popperElementWithArrow = /*#__PURE__*/React__default.cloneElement(popperElement, {
    ref: mergeRefs(setPopperNode, popperElement.ref),
    style: {
      ...styles.popper,
      ...popperElement.props.style
    },
    ...attributes.popper,
    'data-popper-reference-hidden': isAnchorHidden,
    'data-popper-ready': isReady,
    children: arrowElement ? [popperElement.props.children, /*#__PURE__*/React__default.cloneElement(arrowElement, {
      key: 'arrow',
      'aria-hidden': true,
      ref: mergeRefs(setArrowNode, arrowElement.ref),
      style: {
        ...styles.arrow,
        ...arrowElement.props.style
      }
    })] : popperElement.props.children
  });
  const popper = useMemo(() => isFocusLocked ? /*#__PURE__*/React__default.createElement(ReactFocusLock, _extends({}, focusLockProps, {
    disabled: isReady ? focusLockProps === null || focusLockProps === void 0 ? void 0 : focusLockProps.disabled : true // Prevents unintended page scroll when popper element is not yet positioned
  }), popperElementWithArrow) : popperElementWithArrow, [focusLockProps, isFocusLocked, isReady, popperElementWithArrow]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, anchorElement && /*#__PURE__*/React__default.cloneElement(anchorElement, {
    ref: mergeRefs(setAnchorNode, anchorElement.ref)
  }), /*#__PURE__*/React__default.createElement(PopperContextProvider, {
    value: context
  }, /*#__PURE__*/React__default.createElement(Portal, {
    portalTarget: portalTarget
  }, alwaysInDOM ? popper : open ? popper : null)));
};

export { Popper as default };
